import { reactive, readonly } from 'vue'

/**
 * useToast — the app's single, central toast notification service.
 *
 * Why a module-level singleton (not a Pinia store or provide/inject):
 * toasts are fired from components, composables (`useCableMatrixActions`) AND
 * plain utils (`stella-export.utils`). A module singleton is callable from all
 * three; `inject()` only works inside component setup. One global `<Toast>` host
 * (mounted in `App.vue`, teleported above the dialog layer) renders `toastState`.
 *
 * Single-toast model: a new toast replaces the current one. Errors and warnings
 * linger longer than success/info because they need to be read.
 */
export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

interface ToastState {
  show: boolean
  message: string
  variant: ToastVariant
}

const LINGER_MS: Record<ToastVariant, number> = {
  success: 2000,
  info: 2000,
  warning: 5000,
  error: 5000,
}

const state = reactive<ToastState>({
  show: false,
  message: '',
  variant: 'success',
})

let timer: ReturnType<typeof setTimeout> | undefined

function dismiss(): void {
  if (timer) {
    clearTimeout(timer)
    timer = undefined
  }
  state.show = false
}

function show(message: string, variant: ToastVariant): void {
  // Replace any in-flight toast and reset its dismiss timer.
  if (timer) clearTimeout(timer)
  state.message = message
  state.variant = variant
  state.show = true
  timer = setTimeout(dismiss, LINGER_MS[variant])
}

/** Read-only view of the current toast, for the global host component. */
export const toastState = readonly(state)

export function useToast() {
  return {
    success: (message: string) => show(message, 'success'),
    error: (message: string) => show(message, 'error'),
    warning: (message: string) => show(message, 'warning'),
    info: (message: string) => show(message, 'info'),
    dismiss,
  }
}
